import { fromPairs } from 'lodash';
import { useMemo } from 'react';
import { User, UserStatus } from 'api/models';
import type { FieldOptionBase } from 'api/tasks';
import { useUserCustomFieldsAsFilterableFields } from 'components/organisms/module-settings/team-members/section-field/hooks';
import {
  FieldType,
  FilterableField,
  NumberField,
  SingleSelectField,
  SingleUserField,
  SourceType,
  TextField,
} from 'components/organisms/tasks/fields';
import { useGetActiveOrInvitedNonCustomerUsersFromIndexedDb } from 'hooks/use-idb';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { useGetAllPartnerCompanies } from 'queries/partner';
import { useVendorRolesQuery } from 'queries/roles';
import { byLabel } from 'utils/comparators/by-label';
import { fullName } from 'utils/full-name';
import { getRandomColor } from 'utils/get-random-color';
import { sortByText } from 'utils/sort-by-text';

const FIELD_ID_USER = -1000;
const FIELD_ID_ROLE = FIELD_ID_USER - 1;
const FIELD_ID_COMPANY = FIELD_ID_ROLE - 1;
const FIELD_ID_PERMISSION_SET = FIELD_ID_COMPANY - 1;
const FIELD_ID_USER_EMAIL_ID = FIELD_ID_PERMISSION_SET - 1;
const FIELD_ID_TIMESHEET_APPROVER = FIELD_ID_USER_EMAIL_ID - 1;
const FIELD_ID_WEEKLY_CAPACITY = FIELD_ID_TIMESHEET_APPROVER - 1;
const FIELD_ID_COST_RATE = FIELD_ID_WEEKLY_CAPACITY - 1;

export const FIELD_ID_USER_STATUS = FIELD_ID_COST_RATE - 1;

export const UserFieldsName = {
  UserId: 'userId',
  Role: 'role',
  CompanyId: 'companyId',
  EmailId: 'emailId',
  PermissionSetId: 'permissionSetId',
  TimeSheetApprover: 'timeSheetApprover',
  WeeklyCapacity: 'capacity',
  CostRate: 'costRate',
  HolidayCalendarId: 'holidayCalendarId',
  UserStatus: 'userStatus',
};

export const userEmailIdField: TextField = {
  fieldId: FIELD_ID_USER_EMAIL_ID,
  fieldName: UserFieldsName.EmailId,
  fieldLabel: 'Email',
  private: false,
  userCreated: false,
  sourceType: SourceType.User,
  type: FieldType.Text,
};

export const permissionSetTypeField = (options): SingleSelectField => ({
  fieldId: FIELD_ID_PERMISSION_SET,
  fieldName: UserFieldsName.PermissionSetId,
  fieldLabel: 'Permission',
  private: false,
  userCreated: false,
  sourceType: SourceType.User,
  type: FieldType.SingleSelect,
  metaData: {
    options: options.map((x) => ({
      ...x,
      active: true,
      color: getRandomColor(x.value),
    })),
  },
});

export const timesheetApproverField = (usersList: User[]): SingleUserField => ({
  fieldId: FIELD_ID_TIMESHEET_APPROVER,
  fieldName: UserFieldsName.TimeSheetApprover,
  fieldLabel: 'Timesheet approver',
  type: FieldType.SingleUser,
  private: false,
  userCreated: false,
  metaData: {
    users: {
      by_user_id: fromPairs(usersList.map((x) => [x.userId, x])),
      active_user_ids: usersList.map((x) => x.userId),
      non_external_user_ids: usersList.map((x) => x.userId),
      by_project_id: {},
    },
  },
  sourceType: SourceType.User,
});

export const weeklyCapacityField: NumberField = {
  fieldId: FIELD_ID_WEEKLY_CAPACITY,
  fieldName: UserFieldsName.WeeklyCapacity,
  fieldLabel: 'Weekly capacity',
  type: FieldType.Number,
  private: false,
  userCreated: false,
  sourceType: SourceType.User,
};

export const costRateField: NumberField = {
  fieldId: FIELD_ID_COST_RATE,
  fieldName: UserFieldsName.CostRate,
  fieldLabel: 'Hourly cost rate',
  type: FieldType.Number,
  private: false,
  userCreated: false,
  sourceType: SourceType.User,
};

export const userStatusIds = {
  [UserStatus.Active]: 0,
  [UserStatus.Inactive]: 1,
  [UserStatus.Invited]: 2,
};

const userStatusOptions: FieldOptionBase[] = Object.values(UserStatus).map(
  (status) => ({
    label: status === UserStatus.Inactive ? 'DEACTIVATED' : status,
    value: userStatusIds[status],
    active: true,
    color: getRandomColor(
      status === UserStatus.Inactive ? 'DEACTIVATED' : status,
    ),
  }),
);

export const userStatusField: SingleSelectField = {
  fieldId: FIELD_ID_USER_STATUS,
  fieldName: UserFieldsName.UserStatus,
  fieldLabel: 'User status',
  private: false,
  userCreated: false,
  sourceType: SourceType.User,
  type: FieldType.SingleSelect,
  metaData: {
    options: userStatusOptions,
  },
};

export const useUserFieldsAsFilterableFields = (
  { hidePartnerField } = { hidePartnerField: false },
): {
  fields: FilterableField[];
  isFieldsLoading: boolean;
} => {
  const translate = useTranslate();
  const users = useGetActiveOrInvitedNonCustomerUsersFromIndexedDb();
  const sortedUsers = useMemo(
    () =>
      (users ?? []).sort((userA, userB) =>
        sortByText(fullName(userA), fullName(userB)),
      ),
    [users],
  );
  const { data: partnerCompanies, isLoading: isCompaniesLoading } =
    useGetAllPartnerCompanies();

  const { data: roles, isLoading: isRoleLoading } = useVendorRolesQuery();
  const roleOptions = useMemo(
    () =>
      (roles ?? []).map(({ roleId, roleName }) => ({
        label: roleName,
        value: roleId,
        color: getRandomColor(roleId),
        active: true,
      })),
    [roles],
  );
  const teamMemberLabel = useTranslation('team_member');
  const roleLabel = useTranslation('role');

  const userField: SingleUserField = useMemo(
    () => ({
      fieldId: FIELD_ID_USER,
      fieldName: UserFieldsName.UserId,
      fieldLabel: teamMemberLabel,
      type: FieldType.SingleUser,
      private: false,
      userCreated: false,
      metaData: {
        users: {
          by_user_id: fromPairs(sortedUsers.map((x) => [x.userId, x])),
          active_user_ids: sortedUsers.map((x) => x.userId),
          non_external_user_ids: sortedUsers.map((x) => x.userId),
          by_project_id: {},
        },
      },
      sourceType: SourceType.User,
    }),
    [sortedUsers, teamMemberLabel],
  );
  const roleField: SingleSelectField = useMemo(
    () => ({
      fieldId: FIELD_ID_ROLE,
      fieldName: UserFieldsName.Role,
      fieldLabel: roleLabel,
      type: FieldType.SingleSelect,
      private: false,
      userCreated: false,
      metaData: {
        options: roleOptions,
      },
      sourceType: SourceType.User,
    }),
    [roleLabel, roleOptions],
  );
  const companyField: SingleSelectField = useMemo(
    () => ({
      fieldId: FIELD_ID_COMPANY,
      fieldName: UserFieldsName.CompanyId,
      fieldLabel: translate('partners'),
      type: FieldType.SingleSelect,
      private: false,
      userCreated: false,
      metaData: {
        required: true,
        options: (partnerCompanies ?? [])
          .reduce((prev, { companyId, companyName, active }) => {
            return !active
              ? prev
              : [
                  ...prev,
                  {
                    label: companyName,
                    value: companyId,
                    color: getRandomColor(companyId),
                    active: true,
                  },
                ];
          }, [] as FieldOptionBase[])

          .sort(byLabel),
      },
      sourceType: SourceType.User,
    }),
    [partnerCompanies, translate],
  );

  const { isLoading: isUserCustomFieldsLoading, fields: userCustomFields } =
    useUserCustomFieldsAsFilterableFields();

  return useMemo(
    () => ({
      fields: [
        userField,
        roleField,
        ...(!hidePartnerField && (partnerCompanies?.length ?? 0) !== 0
          ? [companyField]
          : []),
        ...userCustomFields,
      ],
      isFieldsLoading:
        isRoleLoading || isUserCustomFieldsLoading || isCompaniesLoading,
    }),
    [
      userField,
      roleField,
      hidePartnerField,
      partnerCompanies?.length,
      companyField,
      userCustomFields,
      isRoleLoading,
      isUserCustomFieldsLoading,
      isCompaniesLoading,
    ],
  );
};
